// Provider, model, agent, routing, Maestro and quota configuration contracts
// (PRD §9.1 RF-CFG-01..14, §8.2 RF-MAESTRO-01/08, §9.4 RF-RATE-01..06).
import { z } from "zod";
import { AGENT_ROLES, AGENT_RUNTIMES, AGENT_SELECTION_MODES, CONFIG_STATES, MEMORY_SCOPES, VALUE_ORIGINS } from "./enums.ts";
import { AGENT_KEY_RE, AGENT_SPECIALTY_RE, integrationGrantSchema } from "./agent-grants.ts";
import { PROTOCOLS } from "./model.ts";
import { DEFAULT_ROUTING_INTELLIGENCE, routingIntelligenceConfigSchema } from "./routing-intelligence.ts";
import { assistantConfigLayerSchema, assistantConfigSchema, DEFAULT_ASSISTANT_CONFIG } from "./assistant.ts";
import { codebaseAnalysisConfigLayerSchema, codebaseAnalysisConfigSchema, DEFAULT_CODEBASE_ANALYSIS } from "./onboarding.ts";

const id = z.uuid();
const timestamp = z.iso.datetime();

/** Field type of a PATCH schema: optional and without the create-time default. */
export type PatchShape<T extends z.ZodRawShape> = {
  [K in keyof T]: z.ZodOptional<T[K] extends z.ZodDefault<infer I> ? I : T[K]>;
};

/**
 * PATCH schema from a create schema: every field optional and WITHOUT defaults, so a partial
 * update never resets fields the client did not send (zod 4 applies defaults inside
 * .partial()). Statically typed via PatchShape.
 */
export function patchSchemaOf<T extends z.ZodRawShape>(schema: z.ZodObject<T>): z.ZodObject<PatchShape<T>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(schema.shape) as [string, z.ZodType][]) {
    shape[key] = (field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : field).optional();
  }
  return z.object(shape) as unknown as z.ZodObject<PatchShape<T>>;
}

/** `integration_module` (D20): credentials and provider settings live in an integration module instance. */
export const PROVIDER_ADAPTERS = ["omniroute", "openrouter", "nvidia_nim", "jev", "openai_responses", "openai_chat", "integration_module"] as const;
export type ProviderAdapter = (typeof PROVIDER_ADAPTERS)[number];

/** Kinds of model target (RF-CFG-04). */
export const MODEL_TARGET_KINDS = ["chat", "embedding", "rerank", "decision"] as const;
export type ModelTargetKind = (typeof MODEL_TARGET_KINDS)[number];
/**
 * D29: the model kinds a connection may serve (migration 0019). A connection restricted to complementary kinds (for
 * example OpenRouter with only `embedding` and `rerank`) can never serve a chat model: creating such a target is
 * refused, routing treats an existing one as unavailable, and the gateway refuses the call before sending it.
 */
export const allowedKindsSchema = z
  .array(z.enum(MODEL_TARGET_KINDS))
  .min(1)
  .max(MODEL_TARGET_KINDS.length)
  .refine((a) => new Set(a).size === a.length, "kinds must not repeat");

// ---------- Provider connections (RF-CFG-01..03, RF-CFG-11) ----------

export const createProviderConnectionSchema = z.object({
  name: z.string().trim().min(1).max(120),
  adapter: z.enum(PROVIDER_ADAPTERS),
  base_url: z.url().max(500).describe("Inference base URL; never inferred from a dashboard URL."),
  auth_mode: z.enum(["bearer", "header", "none"]).default("bearer"),
  quota_pool_id: id.nullable().default(null),
  /** Required for adapter `integration_module` (the `ia` module instance holding key and provider settings). */
  module_instance_id: id.nullable().default(null),
  allowed_kinds: allowedKindsSchema.default([...MODEL_TARGET_KINDS]),
});
export const patchProviderConnectionSchema = patchSchemaOf(createProviderConnectionSchema).extend({ expected_version: z.number().int() });

/** Secret is write-only: PUT stores/replaces, DELETE revokes; responses never include it. */
export const putSecretSchema = z.object({ value: z.string().min(1).max(20_000) });

export const providerConnectionSchema = z.object({
  id,
  name: z.string(),
  adapter: z.enum(PROVIDER_ADAPTERS),
  base_url: z.string(),
  auth_mode: z.enum(["bearer", "header", "none"]),
  quota_pool_id: id.nullable(),
  module_instance_id: id.nullable(),
  /** D29: the model kinds this connection may serve. */
  allowed_kinds: z.array(z.enum(MODEL_TARGET_KINDS)),
  secret: z.object({ state: z.enum(["missing", "active", "revoked"]), masked_hint: z.string().nullable(), updated_at: timestamp.nullable() }),
  state: z.enum(["draft", "validated", "active", "disabled", "auth_failed"]),
  version: z.number().int(),
  created_at: timestamp,
  updated_at: timestamp,
});
export type ProviderConnection = z.infer<typeof providerConnectionSchema>;

export const connectionTestRequestSchema = z.object({
  kind: z.enum(["connection", "catalog", "minimal_inference", "capability", "conformance"]),
  model_target_id: id.optional(),
  /** Inference tests consume tokens; they require an explicit validation budget. */
  max_cost_cents: z.number().nonnegative().optional(),
});

// ---------- Model targets (RF-CFG-04, RF-CFG-07, RF-BACKEND-06/07) ----------

export const CAPABILITY_KEYS = ["streaming", "tool_calling", "multi_turn_tools", "json_schema", "images", "prompt_cache", "reasoning_summary", "reasoning_effort"] as const;
export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

export const capabilityMapSchema = z.partialRecord(z.enum(CAPABILITY_KEYS), z.enum(["declared", "verified", "failed", "unknown"]));

export const createModelTargetSchema = z.object({
  connection_id: id,
  external_model_id: z.string().trim().min(1).max(300).describe("Exact ID or alias; never mapped by similarity."),
  display_name: z.string().trim().min(1).max(120),
  kind: z.enum(MODEL_TARGET_KINDS),
  protocol: z.enum(PROTOCOLS),
  declared_capabilities: capabilityMapSchema.default({}),
  context_window: z.number().int().positive().nullable().default(null),
  max_output_tokens: z.number().int().positive().nullable().default(null),
  pricing: z
    .object({
      currency: z.string().length(3),
      input_per_mtok: z.number().nonnegative().nullable(),
      output_per_mtok: z.number().nonnegative().nullable(),
      /** Pricing v2 (Routing Intelligence, PRD §23): null or absent = unknown, never zero. */
      cache_read_per_mtok: z.number().nonnegative().nullable().optional(),
      cache_write_per_mtok: z.number().nonnegative().nullable().optional(),
      /** Absent = reasoning tokens are billed as output tokens (they are part of the output count). */
      reasoning_per_mtok: z.number().nonnegative().nullable().optional(),
      valid_from: timestamp,
      source: z.string(),
      /** When the price was last revalidated against the provider catalog. */
      verified_at: timestamp.nullable().optional(),
    })
    .nullable()
    .default(null),
  is_free: z.boolean().nullable().default(null).describe("Null = unknown; never assumed free."),
  /** Admin-set: only strong targets may act as Maestro or Maestro substitute (never inferred from a name). */
  strong: z.boolean().default(false),
  quota_pool_id: id.nullable().default(null),
  /** Internal stable alias such as free-coder-primary (RF-BACKEND-07). */
  alias: z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/).nullable().default(null),
});
/**
 * Effective-model policy of a model target (migration 0008), admin-set through the operator
 * bootstrap only. "exact": the gateway must answer with the pinned model or an exact pre-authorized
 * equivalent (AC-88). "router_alias": the target is a router combo (e.g. OmniRoute `auto/coding:free`)
 * whose effective model changes per call; it is accepted only when it matches one of the
 * `accepted_equivalent_models` glob patterns (`*` = any sequence of characters).
 */
export const EFFECTIVE_MODEL_POLICIES = ["exact", "router_alias"] as const;
export type EffectiveModelPolicy = (typeof EFFECTIVE_MODEL_POLICIES)[number];
/** One accepted equivalent: an exact model id or, on router aliases, a glob pattern with `*`. */
export const acceptedEquivalentModelSchema = z
  .string()
  .trim()
  .min(1)
  .max(300)
  .regex(/^[A-Za-z0-9._:/@+*-]+$/, "model ids and patterns may contain letters, digits and . _ : / @ + - * only");

export const patchModelTargetSchema = patchSchemaOf(createModelTargetSchema.omit({ connection_id: true })).extend({ expected_version: z.number().int() });

export const modelTargetSchema = z.object({
  id,
  connection_id: id,
  external_model_id: z.string(),
  display_name: z.string(),
  kind: z.enum(MODEL_TARGET_KINDS),
  protocol: z.enum(PROTOCOLS),
  alias: z.string().nullable(),
  declared_capabilities: capabilityMapSchema,
  verified_capabilities: capabilityMapSchema,
  context_window: z.number().int().nullable(),
  max_output_tokens: z.number().int().nullable(),
  pricing: z.unknown().nullable(),
  is_free: z.boolean().nullable(),
  strong: z.boolean(),
  quota_pool_id: id.nullable(),
  state: z.enum(["unverified", "verified", "invalid", "disabled"]),
  verified_at: timestamp.nullable(),
  version: z.number().int(),
  /** Admin-set (migration 0008); read-only through the API. */
  effective_model_policy: z.enum(EFFECTIVE_MODEL_POLICIES).optional(),
  accepted_equivalent_models: z.array(z.string()).optional(),
});
export type ModelTarget = z.infer<typeof modelTargetSchema>;

// ---------- Agent profiles (RF-CFG-13) ----------

export const createAgentProfileSchema = z.object({
  project_id: id.nullable().default(null),
  name: z.string().trim().min(1).max(120),
  role: z.enum(AGENT_ROLES),
  instructions: z.string().max(50_000).default(""),
  model_target_id: id.nullable().describe("Null only for deterministic executors ('no model')."),
  fallback_model_target_ids: z.array(id).max(10).default([]),
  tools: z.array(z.string().max(100)).max(100).default([]),
  context_limit: z.number().int().positive().nullable().default(null),
  output_limit: z.number().int().positive().nullable().default(null),
  budget: z.object({ max_cost_cents_per_attempt: z.number().nonnegative().nullable(), max_calls_per_attempt: z.number().int().positive().nullable() }).default({ max_cost_cents_per_attempt: null, max_calls_per_attempt: null }),
  concurrency: z.number().int().min(1).max(50).default(1),
  review_policy: z.object({ reviewer: z.enum(["maestro", "critic", "none"]).default("maestro") }).default({ reviewer: "maestro" }),
  /** Registry fields (D20). `key` identifies the agent for delegation and catalog updates. */
  key: z.string().regex(AGENT_KEY_RE).nullable().default(null),
  description: z.string().max(2_000).default(""),
  specialties: z.array(z.string().regex(AGENT_SPECIALTY_RE)).max(20).default([]),
  delegates_to: z.array(z.string().regex(AGENT_KEY_RE)).max(20).default([]),
  consults_to: z.array(z.string().regex(AGENT_KEY_RE)).max(20).default([]),
  memory_scopes: z.array(z.enum(MEMORY_SCOPES)).max(5).default([...MEMORY_SCOPES]),
  integration_grants: z.array(integrationGrantSchema).max(50).default([]),
});
export const patchAgentProfileSchema = patchSchemaOf(createAgentProfileSchema.omit({ project_id: true, role: true })).extend({ expected_version: z.number().int() });

// ---------- Routing configuration with inheritance (RF-CFG-05/06/12/14) ----------

export const roleRouteSchema = z.object({
  agent_profile_ids: z.array(id).max(20),
  default_agent_profile_id: id.nullable(),
  /** Minimum capabilities required for this role's work class. */
  required_capabilities: z.array(z.enum(CAPABILITY_KEYS)).default([]),
  /** Ordered fallback list, conditioned on error class (RF-CFG-08). */
  fallbacks: z
    .array(z.object({ model_target_id: id, on: z.array(z.enum(["rate_limited", "credit_exhausted", "unavailable", "model_not_found", "context_exceeded"])).min(1) }))
    .max(10)
    .default([]),
});

export const routingConfigBodySchema = z.object({
  roles: z.partialRecord(z.enum(AGENT_ROLES), roleRouteSchema),
  maestro: z.object({ model_target_id: id.nullable(), substitute_model_target_ids: z.array(id).max(5).default([]) }),
  free_first: z.object({
    enabled: z.boolean().default(true),
    /** Paid/strong implementation routes allowed only after these versioned triggers (RF-BACKEND-09). */
    escalation_triggers: z.array(z.enum(["capability_missing", "irreducible_context", "reproducible_quality_failure", "risk_class", "material_stagnation"])).default([]),
  }),
  budgets: z.object({
    soft_limit_cents: z.number().nonnegative().nullable(),
    hard_limit_cents: z.number().nonnegative().nullable(),
    window: z.enum(["day", "week", "month"]).default("month"),
  }),
  /** Agent runtime and selection (D20); pinned per run through the config version. */
  agents: z
    .object({
      runtime: z.enum(AGENT_RUNTIMES).default("legacy"),
      selection: z.enum(AGENT_SELECTION_MODES).default("profile"),
    })
    .default({ runtime: "legacy", selection: "profile" }),
  /** Routing Intelligence v2 (D24): flags, thresholds and tier lists; every flag defaults to false. */
  routing_intelligence: routingIntelligenceConfigSchema.default(DEFAULT_ROUTING_INTELLIGENCE),
  /** Assistant panel (2026-09-28, council condition 4): per-question/per-day cost ceilings. */
  assistant: assistantConfigSchema.default(DEFAULT_ASSISTANT_CONFIG),
  /** Existing-project onboarding (2026-09-28): codebase analysis is off by default; a project may only narrow it. */
  codebase_analysis: codebaseAnalysisConfigSchema.default(DEFAULT_CODEBASE_ANALYSIS),
  gauntlet: z.object({
    mode: z.enum(["conventional", "gauntlet"]).default("conventional"),
    required_for: z.array(z.enum(["contract_change", "data_migration", "authentication", "high_risk"])).default([]),
    max_rounds: z.number().int().min(1).max(20).default(4),
    stagnation_rounds: z.number().int().min(1).max(10).default(2),
  }),
});
export type RoutingConfigBody = z.infer<typeof routingConfigBodySchema>;

/** One stored routing layer: every section optional; the assistant and codebase_analysis sections never get defaults (they apply at resolution). */
export const routingConfigLayerSchema = routingConfigBodySchema
  .partial()
  .extend({ assistant: assistantConfigLayerSchema.optional(), codebase_analysis: codebaseAnalysisConfigLayerSchema.optional() });
export type RoutingConfigLayer = z.infer<typeof routingConfigLayerSchema>;

export const createRoutingConfigSchema = z.object({ project_id: id.nullable().default(null), body: routingConfigLayerSchema });

export const routingConfigSchema = z.object({
  id,
  project_id: id.nullable(),
  version: z.number().int(),
  state: z.enum(CONFIG_STATES),
  body: routingConfigLayerSchema,
  validation: z.object({ ok: z.boolean(), impediments: z.array(z.object({ path: z.string(), reason: z.string() })) }).nullable(),
  created_at: timestamp,
  activated_at: timestamp.nullable(),
});
export type RoutingConfig = z.infer<typeof routingConfigSchema>;

/** Effective, inherited configuration (global → project → task) with origin per value. */
export const effectiveRouteRowSchema = z.object({
  role: z.enum(AGENT_ROLES),
  agent_profile_id: id.nullable(),
  agent_name: z.string().nullable(),
  model_target_id: id.nullable(),
  model_display: z.string().nullable(),
  api: z.string().nullable(),
  /** Effective tool set the engine offers for this role (the builder list today; empty when the engine offers none). */
  tools: z.array(z.string()),
  /** The profile's declared tool list: recorded, not enforced by the engine. */
  declared_tools: z.array(z.string()),
  quota_pool: z.string().nullable(),
  fallbacks: z.array(z.string()),
  reviewer: z.string().nullable(),
  origin: z.enum(["global", "project", "task", "unset"]),
  ready: z.boolean(),
  impediment: z.string().nullable(),
});
export const effectiveConfigSchema = z.object({
  project_id: id,
  routing_config_versions: z.object({ global: z.number().int().nullable(), project: z.number().int().nullable() }),
  rows: z.array(effectiveRouteRowSchema),
  maestro: z.object({ model_target_id: id.nullable(), substitutes: z.array(id), ready: z.boolean(), impediment: z.string().nullable() }),
  ready: z.boolean(),
  impediments: z.array(z.string()),
});
export type EffectiveConfig = z.infer<typeof effectiveConfigSchema>;

// ---------- Quota pools (RF-RATE-01/06, RF-EXEC-06/07) ----------

export const quotaPoolSchema = z.object({
  id,
  name: z.string(),
  rpm_limit: z.number().int().positive().nullable(),
  tpm_limit: z.number().int().positive().nullable(),
  concurrency_limit: z.number().int().positive().nullable(),
  budget_limit_cents: z.number().nonnegative().nullable(),
  origin: z.enum(VALUE_ORIGINS),
  confidence: z.number().min(0).max(1).nullable(),
  measured_at: timestamp.nullable(),
  window: z.object({
    used_requests: z.number().int(),
    reserved_requests: z.number().int(),
    in_flight: z.number().int(),
    reset_at: timestamp.nullable(),
    waiting_tasks: z.number().int(),
  }),
  version: z.number().int(),
});
export type QuotaPool = z.infer<typeof quotaPoolSchema>;

export const upsertQuotaPoolSchema = z.object({
  name: z.string().trim().min(1).max(120),
  rpm_limit: z.number().int().positive().nullable(),
  tpm_limit: z.number().int().positive().nullable().default(null),
  concurrency_limit: z.number().int().positive().nullable().default(null),
  budget_limit_cents: z.number().nonnegative().nullable().default(null),
  safety_margin: z.number().min(0).max(0.5).default(0),
  origin: z.enum(VALUE_ORIGINS).default("configured"),
  expected_version: z.number().int().optional(),
});
